import { AnimatePresence, motion } from "framer-motion";
import { Crown, Eye, RotateCcw, Swords, Wrench } from "lucide-react";
import { useEffect, useState } from "react";
import { artAssets } from "../data/site";
import { awardFeat, awardPoints } from "../utils/rewards";
import {
  abilityOf,
  battlePoints,
  BOSS_DECK_IDS,
  bossOfWeek,
  bossClaimedThisWeek,
  buildBossDeck,
  buildEnemyDeck,
  cardById,
  cardPower,
  claimBossWin,
  fetchCloudCollection,
  isApexCard,
  postBattleRecord,
  resolveRound,
  rollDie,
  variantOf,
} from "../utils/tcg";

const shortName = (card) => {
  const variant = variantOf(card);
  return `${card.name.split(",")[0]}${variant ? ` · ${variant}` : ""}`;
};

/* Six-round lane battle against the Black-Ops Net war council. The enemy's
   card is face-up before you commit — pick the counter, abilities fire, dice
   decide the rest. */
export function TcgBattle({ deckIds, user, onRebuild }) {
  const [phase, setPhase] = useState("ready");
  const [enemyDeck, setEnemyDeck] = useState([]);
  const [hand, setHand] = useState([]);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState({ you: 0, net: 0 });
  const [clash, setClash] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState(false);
  const [beatApex, setBeatApex] = useState(false);
  const [result, setResult] = useState(null);
  /* ability-carry state */
  const [pendingYou, setPendingYou] = useState(0);
  const [pendingNet, setPendingNet] = useState(0);
  const [momentum, setMomentum] = useState({ you: 0, net: 0 });
  const [kushiroUsed, setKushiroUsed] = useState({ you: false, net: false });
  const [circuitUsed, setCircuitUsed] = useState({ you: false, net: false });
  const [bossMode, setBossMode] = useState(false);
  const [bossClaimed, setBossClaimed] = useState(() => bossClaimedThisWeek(null));

  const boss = bossOfWeek();
  const bossCard = cardById(BOSS_DECK_IDS[BOSS_DECK_IDS.length - 1]);

  /* linked tags read the vault's boss stamp; guests read the device */
  useEffect(() => {
    fetchCloudCollection().then((cloud) => {
      if (cloud) setBossClaimed(bossClaimedThisWeek(cloud.boss_week ?? null));
    });
  }, []);
  const [peekNext, setPeekNext] = useState(false);
  const [salvagePts, setSalvagePts] = useState(0);

  const playerCards = deckIds.map(cardById).filter(Boolean);
  const enemy = enemyDeck[round];
  const nextEnemy = peekNext ? enemyDeck[round + 1] : null;

  const startBattle = (kind = "council") => {
    const boss = kind === "boss";
    setBossMode(boss);
    setEnemyDeck(boss ? buildBossDeck() : buildEnemyDeck());
    setHand([...playerCards].sort(() => Math.random() - 0.5));
    setRound(0);
    setScore({ you: 0, net: 0 });
    setLog([]);
    setClash(null);
    setResult(null);
    setBeatApex(false);
    setBusy(false);
    setPendingYou(0);
    setPendingNet(0);
    setMomentum({ you: 0, net: 0 });
    setKushiroUsed({ you: false, net: false });
    setCircuitUsed({ you: false, net: false });
    setPeekNext(false);
    setSalvagePts(0);
    setPhase("fighting");
  };

  const finishBattle = (you, net, apexNow, salvage) => {
    const won = you > net;
    const draw = you === net;
    const flawless = you === enemyDeck.length;
    const pts = battlePoints(you, won, flawless, salvage);
    if (pts > 0) awardPoints("tcg", pts);
    if (flawless) awardFeat("DECK MASTER");
    if (apexNow) awardFeat("APEX SLAYER");
    /* weekly boss: beat the Sentinel's deck and its card joins your collection */
    let bossWon = false;
    if (won && bossMode && !bossClaimed) {
      bossWon = true;
      setBossClaimed(true);
      claimBossWin().catch(() => {});
      awardFeat("BOSS BREAKER");
    }
    if (user) postBattleRecord(draw ? "draw" : won ? "win" : "loss", you).catch(() => {});
    setResult({ won, draw, flawless, pts, you, net, salvage, bossWon, bossMode });
    setPhase("done");
    setClash(null);
    setBusy(false);
  };

  const playCard = (card) => {
    if (busy || phase !== "fighting" || !enemy) return;
    setBusy(true);
    const outcome = resolveRound(card, enemy, {
      roundIndex: round,
      youWon: score.you,
      youLost: score.net,
      netWon: score.net,
      netLost: score.you,
      youMomentum: momentum.you,
      netMomentum: momentum.net,
      youCardsLeft: hand.length - 1,
      netCardsLeft: enemyDeck.length - round - 1,
      youPending: pendingYou,
      netPending: pendingNet,
      kushiroYouUsed: kushiroUsed.you,
      kushiroNetUsed: kushiroUsed.net,
      circuitYouUsed: circuitUsed.you,
      circuitNetUsed: circuitUsed.net,
      playerRoll: rollDie(),
      enemyRoll: rollDie(),
    });
    setClash({ playerCard: card, enemyCard: enemy, ...outcome });
    setTimeout(() => {
      const you = score.you + (outcome.playerWins ? 1 : 0);
      const net = score.net + (outcome.playerWins ? 0 : 1);
      const apexNow = beatApex || (outcome.playerWins && isApexCard(enemy));
      const salvage = salvagePts + (outcome.flags.salvage || 0);
      setScore({ you, net });
      setBeatApex(apexNow);
      setSalvagePts(salvage);
      setMomentum({ you: outcome.playerWins ? momentum.you + 1 : 0, net: outcome.playerWins ? 0 : momentum.net + 1 });
      /* every deferred flag type carries into the next unit's charge */
      const NEXT_FLAGS = ["patch", "collapse", "command", "rend", "stun", "burn", "strain", "hymn", "curse", "deliver", "transport", "poison"];
      setPendingYou(NEXT_FLAGS.reduce((sum, key) => sum + (outcome.flags[`${key}You`] || 0), 0));
      setPendingNet(NEXT_FLAGS.reduce((sum, key) => sum + (outcome.flags[`${key}Net`] || 0), 0));
      setKushiroUsed({ you: kushiroUsed.you || !!outcome.flags.kushiroYou, net: kushiroUsed.net || !!outcome.flags.kushiroNet });
      setCircuitUsed({ you: circuitUsed.you || !!outcome.flags.circuitYou, net: circuitUsed.net || !!outcome.flags.circuitNet });
      if (outcome.flags.peekYou) setPeekNext(true);
      setLog((entries) =>
        [
          `R${round + 1} — ${shortName(card)} ${outcome.playerWins ? "crushes" : "falls to"} ${shortName(enemy)} (${outcome.pTotal} vs ${outcome.eTotal})`,
          ...entries,
        ].slice(0, 5),
      );
      setHand((current) => current.filter((c) => c.id !== card.id));
      if (round >= enemyDeck.length - 1) {
        finishBattle(you, net, apexNow, salvage);
      } else {
        setRound(round + 1);
        setClash(null);
        setBusy(false);
      }
    }, 2100);
  };

  if (phase === "ready") {
    return (
      <div className="battle-ready" data-testid="battle-ready">
        <div className="battle-ready-deck">
          {playerCards.map((card) => (
            <span key={card.id} className={`holo-card rarity-${card.rarity}`}>
              <img src={card.image} alt={`${card.name} card`} />
            </span>
          ))}
        </div>
        <div className="battle-ready-copy">
          <h3>THE NET WAR COUNCIL IS WAITING.</h3>
          <p>
            Six rounds. The council fields its units weakest-first — fodder up front, apex horrors at the
            end. Their card is face-up before you commit, so spend your heavy units wisely. Power reads
            ATK + DEF + SCRAP, surveillance outranks, and every printed ability now fires for real —
            silence their tricks with Volo, steal them with Circe, or close with Echo's Final Stand.
          </p>
          <div className="battle-start-row">
            <button type="button" className="battle-start-button" onClick={() => startBattle("council")} data-testid="battle-start-button">
              <Swords size={16} /> START BATTLE
            </button>
          </div>
          {bossCard && (
            <div className="boss-panel" data-testid="boss-panel">
              <img src={bossCard.image} alt={`${boss.bounty} boss card`} className="boss-thumb" />
              <div className="boss-panel-copy">
                <strong>WEEKLY BOSS — {boss.title}</strong>
                <p>
                  {bossClaimed
                    ? "BEATEN THIS CYCLE — REMATCH FOR PTS UNTIL NEXT WEEK RESETS THE BOUNTY."
                    : `${boss.pronoun} FIXED EXHIBITION DECK, SIX ROUNDS. BRING DOWN THE ${boss.short} AND ${boss.pronoun} CARD JOINS YOUR COLLECTION.`}
                </p>
                <button type="button" className="boss-start-button" onClick={() => startBattle("boss")} data-testid="boss-start-button">
                  <Crown size={14} /> {bossClaimed ? `REMATCH THE ${boss.short}` : "CHALLENGE THE BOSS"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="battle-arena" data-testid="battle-arena">
      <div className="battle-scoreboard" data-testid="battle-scoreboard">
        <span className="score-you">YOU {score.you}</span>
        <span className="score-round" data-testid="battle-round">ROUND {Math.min(round + 1, 6)} / 6</span>
        <span className="score-net">NET {score.net}</span>
      </div>

      {phase === "fighting" && enemy && (
        <div className="battle-stage">
          <div className="battle-enemy-zone">
            <span className="zone-label">{bossMode ? `${boss.title} — WEEKLY BOSS DECK` : `INCOMING — ${enemy.faction}`}</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={round}
                className="flip-card battle-enemy-flip"
                initial={{ opacity: 0, rotateY: -180 }}
                animate={{ opacity: 1, rotateY: 0 }}
                exit={{ opacity: 0, rotateY: 180 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className={`flip-face flip-front holo-card rarity-${enemy.rarity}`}>
                  <img src={enemy.image} alt={`${enemy.name} enemy card`} data-testid="battle-enemy-card" />
                </span>
                <span className="flip-face flip-back">
                  <img src={artAssets.cardBack} alt="" aria-hidden="true" />
                </span>
              </motion.span>
            </AnimatePresence>
            <span className="zone-stats" data-testid="battle-enemy-power">
              {shortName(enemy)} · PWR {cardPower(enemy)} · SURVEIL {enemy.stats.surveillance}
            </span>
            {abilityOf(enemy) && (
              <span className="zone-ability" data-testid="battle-enemy-ability">
                {abilityOf(enemy).name} — {abilityOf(enemy).text}
              </span>
            )}
            {nextEnemy && (
              <span className="enemy-next-up" data-testid="enemy-next-up">
                <Eye size={12} /> NEXT UP: {shortName(nextEnemy)} · PWR {cardPower(nextEnemy)}
              </span>
            )}
            {enemyDeck.length - round - 1 > 0 && (
              <span className="enemy-stack" data-testid="enemy-stack">
                {Array.from({ length: enemyDeck.length - round - 1 }).map((_, i) => (
                  <img key={i} src={artAssets.cardBack} alt="" aria-hidden="true" />
                ))}
                <small>{enemyDeck.length - round - 1} IN THE HOLE</small>
              </span>
            )}
          </div>

          <div className="battle-clash-zone" data-testid="battle-clash-zone">
            <AnimatePresence mode="wait">
              {clash ? (
                <motion.div
                  key={`clash-${round}`}
                  className={`battle-clash ${clash.playerWins ? "you-win" : "net-win"}`}
                  data-testid="battle-clash"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="clash-side">
                    <img src={clash.playerCard.image} alt={`${clash.playerCard.name} card`} />
                    <strong>{clash.pTotal}</strong>
                    <small>PWR + {clash.pEdge} EDGE + D8({clash.pRoll})</small>
                  </div>
                  <div className="clash-center">
                    <span className="clash-verdict" data-testid="clash-verdict">
                      {clash.playerWins ? "ROUND TAKEN" : "ROUND LOST"}
                    </span>
                    {clash.mods.length > 0 && (
                      <div className="clash-mods" data-testid="clash-mods">
                        {clash.mods.map((mod, index) => (
                          <span key={index} className={`mod-chip ${mod.side === "you" ? "mod-you" : "mod-net"}`}>
                            {mod.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="clash-side">
                    <img src={clash.enemyCard.image} alt={`${clash.enemyCard.name} card`} />
                    <strong>{clash.eTotal}</strong>
                    <small>PWR + {clash.eEdge} EDGE + D8({clash.eRoll})</small>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key={`idle-${round}`}
                  className="battle-idle"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <Swords size={26} />
                  <p>PICK YOUR COUNTER FROM THE HAND BELOW.</p>
                  {(pendingYou !== 0 || pendingNet !== 0) && (
                    <p className="battle-pending" data-testid="battle-pending">
                      {pendingYou !== 0 && `YOUR NEXT UNIT ${pendingYou > 0 ? "+" : ""}${pendingYou} · `}
                      {pendingNet !== 0 && `NET NEXT UNIT ${pendingNet > 0 ? "+" : ""}${pendingNet}`}
                    </p>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      )}

      {phase === "fighting" && (
        <div className="battle-hand" data-testid="battle-hand">
          {hand.map((card) => (
            <button
              type="button"
              key={card.id}
              className={`battle-hand-card rarity-${card.rarity}`}
              onClick={() => playCard(card)}
              disabled={busy}
              data-testid={`battle-hand-card-${card.id}`}
              aria-label={`Play ${card.name} this round`}
            >
              <img src={card.image} alt={`${card.name} card`} />
              <span>{shortName(card)}</span>
              <small>PWR {cardPower(card)} · SV {card.stats.surveillance}</small>
              {abilityOf(card) && <em className="hand-ability">{abilityOf(card).name}</em>}
            </button>
          ))}
        </div>
      )}

      {log.length > 0 && phase === "fighting" && (
        <div className="battle-log" data-testid="battle-log">
          {log.map((entry, index) => (
            <p key={`${entry}-${index}`}>{entry}</p>
          ))}
        </div>
      )}

      {phase === "done" && result && (
        <motion.div
          className={`battle-result ${result.won ? "victory" : result.draw ? "draw" : "defeat"}`}
          data-testid="battle-result"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <h3 data-testid="battle-result-title">
            {result.won ? "THE NET KNEELS. VICTORY." : result.draw ? "STALEMATE ON THE HIGHWAY." : "THE NET SWALLOWS YOU."}
          </h3>
          <p className="battle-result-score">
            {result.you} — {result.net} {result.flawless && "· FLAWLESS SWEEP"}
          </p>
          <p className="battle-result-points" data-testid="battle-points-earned">
            {result.pts > 0 ? `+${result.pts} PTS BANKED TO YOUR STASH` : "NO PTS THIS RUN — TAKE ROUNDS TO EARN."}
            {result.salvage > 0 && <small> (includes +{result.salvage} salvaged)</small>}
          </p>
          {result.bossWon && (
            <p className="battle-boss-claim" data-testid="boss-claim-note">
              BOUNTY CLAIMED — {boss.bounty} JOINS YOUR COLLECTION.
            </p>
          )}
          <div className="battle-actions">
            <button type="button" onClick={() => startBattle(bossMode ? "boss" : "council")} data-testid="battle-rematch-button">
              <RotateCcw size={15} /> RUN IT BACK
            </button>
            <button type="button" className="is-secondary" onClick={onRebuild} data-testid="battle-rebuild-button">
              <Wrench size={15} /> REBUILD DECK
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
